import * as React from "react";
import PropTypes from "prop-types";

import { createHocDisplayName } from "../../../../TypingUtils";

export interface UnitLayoutingContext {
    paddingLeft: number;
    paddingRight: number;
    marginTop?: number;
    addMarginTop?: (marginSize: number) => void;
    unitContextRef?: React.MutableRefObject<HTMLElement | null>;
}

const UnitLayoutingContextTools = React.createContext<UnitLayoutingContext>({
    paddingLeft: 0,
    paddingRight: 0,
});

interface UnitLayoutingContextInjectableProps {
    unitLayouting: UnitLayoutingContext;
}

export function useUnitLayoutingContext(): UnitLayoutingContext {
    return React.useContext(UnitLayoutingContextTools);
}

interface UnitLayoutingContextProviderProps {
    value: UnitLayoutingContext;
    children: React.ReactNode;
}

export const unitLayoutingContextTypes = {
    paddingLeft: PropTypes.number,
    paddingRight: PropTypes.number,
};

interface UnitLayoutingContextProviderState {
    marginTop?: number;
}

export class UnitLayoutingContextProvider extends React.Component<
    UnitLayoutingContextProviderProps,
    UnitLayoutingContextProviderState
> {
    public constructor(props: UnitLayoutingContextProviderProps) {
        super(props);
        this.UnitRef = React.createRef<HTMLElement | null>() as React.MutableRefObject<HTMLElement>;
        this.state = {
            marginTop: undefined,
        };
    }
    public UnitRef: React.MutableRefObject<HTMLElement | null>;
    public static childContextTypes = unitLayoutingContextTypes;

    public getChildContext() {
        return {
            paddingLeft: this.props.value.paddingLeft,
            paddingRight: this.props.value.paddingRight,
        };
    }

    public render(): JSX.Element {
        return (
            <UnitLayoutingContextTools.Provider
                value={{
                    paddingLeft: this.props.value.paddingLeft,
                    paddingRight: this.props.value.paddingRight,
                    marginTop: this.state.marginTop,
                    addMarginTop: (marginSize: number) =>
                        this.setState({ marginTop: (this.state.marginTop ?? 40) + marginSize }),
                    unitContextRef: this.UnitRef,
                }}>
                {this.props.children}
            </UnitLayoutingContextTools.Provider>
        );
    }
}

export function withUnitLayoutingContext<P extends UnitLayoutingContextInjectableProps>(
    WrappedComponent: React.ComponentType<P & { unitLayouting: UnitLayoutingContext }>
): React.ComponentType<P> {
    function WithUnitLayoutingContextWrapper(props: P): JSX.Element {
        const unitLayouting = useUnitLayoutingContext();

        return <WrappedComponent {...props} unitLayouting={unitLayouting} />;
    }
    WithUnitLayoutingContextWrapper.displayName = createHocDisplayName(WrappedComponent, "UnitLayoutingContext");
    return WithUnitLayoutingContextWrapper;
}
